/**
 * Authentication service for Cognito integration
 * Handles login-only functionality - signup/registration is disabled
 * Supports environment-specific configuration for deployed environments
 */

import { getConfig, isProduction, isDevelopment } from '../config';

// Get environment-specific Cognito configuration
const getCognitoConfig = () => {
  const config = getConfig();
  return {
    userPoolId: config.cognitoUserPoolId,
    clientId: config.cognitoUserPoolClientId,
    hostedUiUrl: config.cognitoHostedUiUrl,
    region: config.cognitoRegion,
  };
};

export interface AuthUser {
  username: string;
  email?: string;
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

/**
 * AuthService handles user authentication for existing accounts only.
 * Signup/registration functionality has been removed - only login is supported.
 */
class AuthService {
  private user: AuthUser | null = null;

  constructor() {
    // Try to restore user from localStorage on initialization
    this.restoreUserFromStorage();
  }

  private restoreUserFromStorage(): void {
    try {
      const storedUser = localStorage.getItem('streaming_auth_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        // Check if token is still valid (basic check)
        if (user.idToken && !this.isTokenExpired(user.idToken)) {
          this.user = user;
        } else {
          localStorage.removeItem('streaming_auth_user');
        }
      }
    } catch (error) {
      console.error('Error restoring user from storage:', error);
      localStorage.removeItem('streaming_auth_user');
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  private saveUserToStorage(user: AuthUser): void {
    try {
      localStorage.setItem('streaming_auth_user', JSON.stringify(user));
    } catch (error) {
      console.error('Error saving user to storage:', error);
    }
  }

  private clearUserFromStorage(): void {
    localStorage.removeItem('streaming_auth_user');
  }

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      const cognitoConfig = getCognitoConfig();
      
      // Validate configuration before attempting login
      const configValidation = this.validateCognitoConfig();
      if (!configValidation.isValid) {
        throw new Error(`Cognito configuration error: ${configValidation.errors.join(', ')}`);
      }
      
      // Use environment-specific Cognito endpoint
      const cognitoEndpoint = `https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`;
      
      const response = await fetch(cognitoEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: cognitoConfig.clientId,
          AuthParameters: {
            USERNAME: credentials.username,
            PASSWORD: credentials.password,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      
      if (data.AuthenticationResult) {
        const user: AuthUser = {
          username: credentials.username,
          accessToken: data.AuthenticationResult.AccessToken,
          idToken: data.AuthenticationResult.IdToken,
          refreshToken: data.AuthenticationResult.RefreshToken,
        };

        this.user = user;
        this.saveUserToStorage(user);
        
        // Log successful login in development
        if (isDevelopment()) {
          console.log('[Auth] Login successful for user:', credentials.username);
        }
        
        return user;
      } else {
        throw new Error('Authentication failed - no tokens received');
      }
    } catch (error) {
      console.error('[Auth] Login error:', error);
      
      // Provide environment-specific error messages
      if (isProduction()) {
        throw new Error('Login failed. Please check your credentials and try again.');
      } else {
        throw new Error(`Login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }

  logout(): void {
    if (isDevelopment()) {
      console.log('[Auth] Logging out user');
    }
    
    this.user = null;
    this.clearUserFromStorage();
  }

  // Refresh authentication tokens
  async refreshTokens(): Promise<AuthUser | null> {
    if (!this.user?.refreshToken) {
      return null;
    }

    try {
      const cognitoConfig = getCognitoConfig();
      const cognitoEndpoint = `https://cognito-idp.${cognitoConfig.region}.amazonaws.com/`;
      
      const response = await fetch(cognitoEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: cognitoConfig.clientId,
          AuthParameters: {
            REFRESH_TOKEN: this.user.refreshToken,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      if (data.AuthenticationResult) {
        const updatedUser: AuthUser = {
          ...this.user,
          accessToken: data.AuthenticationResult.AccessToken,
          idToken: data.AuthenticationResult.IdToken,
          // Refresh token may or may not be updated
          refreshToken: data.AuthenticationResult.RefreshToken || this.user.refreshToken,
        };

        this.user = updatedUser;
        this.saveUserToStorage(updatedUser);
        
        if (isDevelopment()) {
          console.log('[Auth] Tokens refreshed successfully');
        }
        
        return updatedUser;
      } else {
        throw new Error('Token refresh failed - no tokens received');
      }
    } catch (error) {
      console.error('[Auth] Token refresh error:', error);
      
      // Clear invalid tokens
      this.logout();
      return null;
    }
  }

  getCurrentUser(): AuthUser | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.user !== null && !this.isTokenExpired(this.user.idToken);
  }

  async getAuthToken(): Promise<string | null> {
    if (!this.user) {
      return null;
    }

    // Check if token is expired
    if (this.isTokenExpired(this.user.idToken)) {
      // Try to refresh tokens
      const refreshedUser = await this.refreshTokens();
      if (!refreshedUser) {
        return null;
      }
    }

    return this.user.idToken;
  }

  // Synchronous version for backward compatibility
  getAuthTokenSync(): string | null {
    if (this.isAuthenticated() && this.user) {
      return this.user.idToken;
    }
    return null;
  }

  // Get the Cognito Hosted UI login URL (login-only, no signup)
  getHostedUILoginUrl(): string {
    const cognitoConfig = getCognitoConfig();
    const redirectUri = encodeURIComponent(this.getCallbackUrl());
    
    // Explicitly use login endpoint to ensure no signup options are presented
    return `${cognitoConfig.hostedUiUrl}/login?client_id=${cognitoConfig.clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${redirectUri}`;
  }

  // Get the Cognito Hosted UI logout URL
  getHostedUILogoutUrl(): string {
    const cognitoConfig = getCognitoConfig();
    const redirectUri = encodeURIComponent(this.getLogoutUrl());
    
    return `${cognitoConfig.hostedUiUrl}/logout?client_id=${cognitoConfig.clientId}&logout_uri=${redirectUri}`;
  }

  // Get environment-specific callback URL
  private getCallbackUrl(): string {
    if (isProduction()) {
      // In production, use the deployed domain
      return `${window.location.origin}/callback`;
    } else if (isDevelopment()) {
      // In development, use localhost
      return `${window.location.origin}/callback`;
    } else {
      // Staging or other environments
      return `${window.location.origin}/callback`;
    }
  }

  // Get environment-specific logout URL
  private getLogoutUrl(): string {
    if (isProduction()) {
      // In production, redirect to home page after logout
      return `${window.location.origin}/`;
    } else if (isDevelopment()) {
      // In development, redirect to home page
      return `${window.location.origin}/`;
    } else {
      // Staging or other environments
      return `${window.location.origin}/`;
    }
  }

  // Validate Cognito configuration for current environment
  validateCognitoConfig(): { isValid: boolean; errors: string[] } {
    const cognitoConfig = getCognitoConfig();
    const errors: string[] = [];

    if (!cognitoConfig.userPoolId) {
      errors.push('Cognito User Pool ID is not configured');
    }

    if (!cognitoConfig.clientId) {
      errors.push('Cognito Client ID is not configured');
    }

    if (!cognitoConfig.hostedUiUrl) {
      errors.push('Cognito Hosted UI URL is not configured');
    }

    if (!cognitoConfig.region) {
      errors.push('Cognito region is not configured');
    }

    // Validate URL format in production
    if (isProduction() && cognitoConfig.hostedUiUrl && !cognitoConfig.hostedUiUrl.startsWith('https://')) {
      errors.push('Cognito Hosted UI URL must use HTTPS in production');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Get current Cognito configuration (for debugging)
  getCognitoConfiguration() {
    return {
      ...getCognitoConfig(),
      callbackUrl: this.getCallbackUrl(),
      logoutUrl: this.getLogoutUrl(),
      environment: getConfig().environment,
    };
  }
}

export const authService = new AuthService();
export default authService;